import { useEffect, useRef } from 'react';

import { useFetcher } from '@remix-run/react';
import { useQueryClient } from '@tanstack/react-query';

import { type ToastData, ToastStatus } from '~/hooks/useToast';
import toast from '~/utils/toast';

type CommunityFetcherResponse = {
  toast?: ToastData;
};

export const useCommunityFetcher = <T extends CommunityFetcherResponse>(
  key: string,
  { queryKey, onSubmissionComplete }: { queryKey: string[]; onSubmissionComplete?: () => void },
) => {
  const fetcher = useFetcher<T>({ key });
  const queryClient = useQueryClient();
  const prevStateRef = useRef(fetcher.state);
  const isProcessing = fetcher.state !== 'idle';

  useEffect(() => {
    const wasActive = prevStateRef.current !== 'idle';
    prevStateRef.current = fetcher.state;
    if (wasActive && fetcher.state === 'idle' && fetcher.data) {
      queryClient.invalidateQueries({ queryKey });

      if (fetcher.data.toast) {
        const { status, title, message } = fetcher.data.toast;
        const toastMessage = title ? `${title}\n${message}` : message;
        if (status === ToastStatus.ERROR) {
          toast.error(toastMessage, { id: key });
        } else {
          toast.success(toastMessage, { id: key });
        }
      }

      onSubmissionComplete?.();
    }
  }, [fetcher.state, fetcher.data, queryClient, key, queryKey, onSubmissionComplete]);

  return { fetcher, isProcessing };
};
